import React, { useState, useContext, useMemo } from 'react';
import { AppContext } from '../context/AppContext';
import MobileDashboardTab from './tabs/MobileDashboardTab';
import MobileMailTab from './tabs/MobileMailTab';
import MobileLeaveExpenseTab from './tabs/MobileLeaveExpenseTab';
import MobileAddressBookTab from './tabs/MobileAddressBookTab';
import MobileSettingsTab from './tabs/MobileSettingsTab';
import LeaveApprovalManagement from '../tabs/AdminDashboard/LeaveApprovalManagement';
import AdminDashboard from '../tabs/AdminDashboard/AdminDashboard';
import MobileContractTab from './tabs/MobileContractTab';
import { 
  Home, 
  Mail, 
  Calendar, 
  Users, 
  Settings, 
  Monitor, 
  ShieldCheck,
  ChevronLeft,
  FileText
} from 'lucide-react';

const MobileApp = ({ onSwitchToDesktop }) => {
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard' | 'webmail' | 'leave' | 'expense' | 'contract' | 'addressbook' | 'settings' | 'approval' | 'admin' | 'dynamodb'
  const [mailComposeInitial, setMailComposeInitial] = useState(null);
  const { currentUser, emails, currentLogo, contracts } = useContext(AppContext);

  const [mailUnreadInitial, setMailUnreadInitial] = useState(false);

  const handleNavigateUnreadMail = () => {
    setMailUnreadInitial(true);
    setActiveTab('webmail');
  };

  const handleComposeTo = (recipientEmail) => {
    setMailComposeInitial({
      to: recipientEmail,
      isComposing: true
    });
    setActiveTab('webmail');
  };

  // Unread emails badge for current user (Aliases & OwnerEmail included)
  const unreadCount = emails.filter(e => {
    const raw = (currentUser?.Email || '').toLowerCase().trim();
    const aliasList = (currentUser?.Aliases || '').toLowerCase().split(',').map(a => a.trim()).filter(Boolean);
    const allUserAddresses = [raw, ...aliasList].filter(Boolean);
    const recipient = (e.Recipient || '').toLowerCase();
    const owner = (e.OwnerEmail || '').toLowerCase().trim();
    const isForMe = allUserAddresses.some(addr => recipient.includes(addr) || owner === addr);
    return isForMe && !e.IsRead;
  }).length;

  // Pending contracts badge for user
  const pendingContractsCount = useMemo(() => {
    if (!currentUser) return 0;
    const myEmail = (currentUser.Email || '').toLowerCase();
    const myId = currentUser.UserID;
    const myName = currentUser.Name;
    return contracts.filter(c => {
      const matchMe = (c.UserID && c.UserID === myId) || (c.UserEmail && c.UserEmail.toLowerCase() === myEmail) || (c.UserName === myName);
      return matchMe && c.Status === 'PENDING';
    }).length;
  }, [contracts, currentUser]);

  const getHeaderTitle = () => {
    switch (activeTab) {
      case 'dashboard': return '홈';
      case 'webmail': return '사내 메일';
      case 'leave': return '연차 & 결재';
      case 'expense': return '경비 청구';
      case 'contract': return '전자계약';
      case 'addressbook': return '임직원 주소록';
      case 'settings': return '내 정보 및 설정';
      case 'approval': return '결재 승인 처리';
      case 'admin': return '전사 관리자';
      default: return '클라우메일';
    }
  };

  return (
    <div className="flex flex-col h-[100dvh] w-full max-w-full bg-[#F4F5F9] text-slate-900 overflow-hidden font-sans select-none safe-top">
      
      {/* 1. Mobile Top App Bar */}
      <header className="h-14 bg-white/95 backdrop-blur-md border-b border-[#EAECF0] px-4 flex items-center justify-between flex-shrink-0 z-30 shadow-2xs">
        <div className="flex items-center gap-2">
          {activeTab !== 'dashboard' ? (
            <button
              type="button"
              onClick={() => setActiveTab('dashboard')}
              className="p-1.5 -ml-1.5 text-slate-700 hover:bg-slate-100 rounded-full cursor-pointer transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          ) : (
            <img 
              src={currentLogo} 
              alt="Clouflake" 
              className="h-6 object-contain"
            />
          )}
          <span className="font-extrabold text-sm text-slate-900 tracking-tight">{getHeaderTitle()}</span>
        </div>

        {/* Switch to Desktop button */}
        <button
          type="button"
          onClick={onSwitchToDesktop}
          className="p-2 text-slate-600 hover:text-[#5C5FEF] hover:bg-indigo-50/70 border border-slate-200/80 rounded-xl text-xs font-bold flex items-center gap-1 transition-all shadow-2xs cursor-pointer"
          title="PC 버전으로 보기"
        >
          <Monitor className="w-4 h-4 text-[#5C5FEF]" />
          <span className="text-[11px] font-bold">PC버전</span>
        </button>
      </header>

      {/* 2. Main Mobile Page View Container */}
      <main className="flex-1 overflow-y-auto bg-[#F4F5F9] pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))]">
        {activeTab === 'dashboard' && (
          <MobileDashboardTab 
            setActiveTab={setActiveTab} 
            onNavigateUnreadMail={handleNavigateUnreadMail} 
          />
        )}
        {activeTab === 'webmail' && (
          <MobileMailTab 
            initialCompose={mailComposeInitial} 
            onClearInitialCompose={() => setMailComposeInitial(null)} 
            initialUnreadOnly={mailUnreadInitial}
            onClearInitialUnreadOnly={() => setMailUnreadInitial(false)}
          />
        )}
        {activeTab === 'leave' && <MobileLeaveExpenseTab initialMode="leave" />}
        {activeTab === 'expense' && <MobileLeaveExpenseTab initialMode="expense" />}
        {activeTab === 'contract' && <MobileContractTab />}
        {activeTab === 'addressbook' && <MobileAddressBookTab onComposeTo={handleComposeTo} />}
        {activeTab === 'settings' && <MobileSettingsTab onSwitchToDesktop={onSwitchToDesktop} setActiveTab={setActiveTab} />}
        {activeTab === 'approval' && (
          <div className="p-3 pb-20">
            <LeaveApprovalManagement />
          </div>
        )}
        {activeTab === 'admin' && (
          <div className="p-3 pb-20">
            <AdminDashboard />
          </div>
        )}
      </main>

      {/* 3. Mobile Bottom Navigation Bar (Safe-Area Aware) */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-[#EAECF0] flex items-center justify-around px-2 z-40 shadow-lg pb-[env(safe-area-inset-bottom,0px)] pt-2 min-h-16">
        {[
          { id: 'dashboard', label: '홈', icon: Home },
          { id: 'webmail', label: '메일', icon: Mail, badge: unreadCount },
          { id: 'leave', label: '연차/결재', icon: Calendar },
          { id: 'contract', label: '전자계약', icon: FileText, badge: pendingContractsCount },
          { id: 'settings', label: '설정', icon: Settings }
        ].map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id || (tab.id === 'leave' && activeTab === 'expense');
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-1 flex flex-col items-center justify-center gap-1 relative transition-all cursor-pointer ${
                isActive ? 'text-[#5C5FEF] font-extrabold scale-105' : 'text-slate-400 font-medium hover:text-slate-600'
              }`}
            >
              <div className="relative">
                <Icon className={`w-5 h-5 ${isActive ? 'stroke-[2.5]' : 'stroke-2'}`} />
                {tab.badge > 0 && (
                  <span className="absolute -top-1 -right-2.5 bg-[#5C5FEF] text-white text-[9px] font-black px-1.5 py-0.2 rounded-full ring-2 ring-white">
                    {tab.badge}
                  </span>
                )}
              </div>
              <span className="text-[10px] tracking-tight">{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default MobileApp;
